<script>
/**
 * Contratos do cliente em foco.
 *
 * ⚠️ Este é o ÚNICO ponto do painel que chama API — os outros só desenham. O
 * download precisa do base64, que vem por request próprio (`/export`), e não
 * cabia na carga inicial: baixar o arquivo de todo contrato ao abrir a lateral
 * seria trazer megabytes que quase ninguém pede.
 *
 * Componente BURRO: recebe a lista já buscada e só desenha.
 */
import { apiDocuments } from '@modules/documents/api/apiDocuments'
import { HTTP_OK } from '@modules/aawz-intelligence-v2/constants/Http'
import { contadorDeLista } from '@modules/aawz-intelligence-v2/utils/cliente-em-foco/contador-de-lista'

const ROTULO = 'Contratos'
const TAMANHO_ICONE = 14
const TAMANHO_CHEVRON = 12
const ICONE_BAIXAR = 'document-download'
// Dois (era 3 até 14/09/2026): a seção divide a última linha do painel com as
// transcrições, e o painel tem de caber no piso de 660px do bloco superior —
// conta no `<style>` do `ia-cliente-painel.vue`. O total vai para o contador.
const MAX_ITENS = 2
// Uma linha só: a seção costuma ter poucos itens, e um esqueleto mais alto que
// o conteúdo final faz a coluna encolher quando o dado chega.
const LINHAS_PENDENTES = [{ chave: 'p1' }]
const CAIXA_NOME = { width: '70%', height: '13px' }
const ICONE = 'document-text'

export default {
  name: 'IaClienteContratos',
  props: {
    // `documents/documentsByClient`, cru.
    contratos: { type: Array, default: () => [] },
    carregando: { type: Boolean, default: false },
    navegavel: { type: Boolean, default: false },
  },
  data() {
    return {
      LINHAS_PENDENTES,
      CAIXA_NOME,
      TAMANHO_ICONE,
      TAMANHO_CHEVRON,
      ICONE,
      ICONE_BAIXAR,
      // Id do contrato em download. Um por vez basta: o botão vira spinner e o
      // resto da lista segue clicável.
      baixando: '',
    }
  },
  computed: {
    rotulo() {
      return ROTULO
    },
    itens() {
      return (this.contratos || []).slice(0, MAX_ITENS).map((doc, i) => ({
        chave: 'doc:' + (doc.id_documento || i),
        id: doc.id_documento || doc.id || '',
        nome: doc.nome_documento || 'Documento',
      }))
    },
    // "2 de 5" — aqui o total é exato, porque a lista inteira chega por prop.
    // Fórmula única em `contadorDeLista`.
    contador() {
      return contadorDeLista({ exibidos: this.itens.length, total: (this.contratos || []).length })
    },
    vazio() {
      return !this.carregando && !this.itens.length
    },
  },
  methods: {
    aoClicar() {
      if (!this.navegavel) return
      this.$emit('abrir')
    },
    /**
     * Baixa o contrato. O `apiDocuments` busca o base64 e dispara o download —
     * é o mesmo caminho do módulo de documentos, não uma segunda implementação.
     *
     * ⚠️ Sem `id` não há o que baixar, e o botão nem aparece: documento
     * importado de fora pode chegar sem ele.
     */
    async baixar(item) {
      if (!item.id || this.baixando) return
      this.baixando = item.id
      try {
        const r = await apiDocuments.downloadDocument({ id: item.id })
        // Sem isto a falha era só o spinner sumindo — nada baixava e nada avisava.
        if (!r || r.status !== HTTP_OK) this.$showSnack(r || {})
      } finally {
        this.baixando = ''
      }
    },
  },
}
</script>

<template>
  <div class="ia-cliente-contratos">
    <component
      :is="navegavel ? 'button' : 'div'"
      class="ia-cliente-contratos__rotulo ia-texto-selo"
      :type="navegavel ? 'button' : null"
      @click="aoClicar"
    >
      <span>{{ rotulo }}</span>
      <!-- ⚠️ `arrow-left` aponta para a DIREITA — ver a nota no `ia-insight-card`. -->
      <icon-svg
        v-if="navegavel"
        name="arrow-left"
        :size="TAMANHO_CHEVRON"
        color="var(--neutral-600)"
      />
      <span v-if="!carregando" class="ia-sidebar-contador ml-auto" data-test="ia-cliente-contratos-contador">
        {{ contador }}
      </span>
    </component>

    <ul v-if="carregando" class="ia-cliente-contratos__lista">
      <li v-for="linha in LINHAS_PENDENTES" :key="linha.chave">
        <icon-svg :name="ICONE" :size="TAMANHO_ICONE" color="var(--neutral-300)" />
        <!-- ⚠️ O `span` com `flex: 1` é o que dá largura à caixa: a do esqueleto
             é em PORCENTAGEM e, solta no `li`, resolvia contra zero — a linha
             ficava vazia, como se a seção não tivesse esqueleto. -->
        <span class="ia-cliente-contratos__pendente">
          <BaseLoadingSkeleton v-bind="CAIXA_NOME" />
        </span>
      </li>
    </ul>

    <p v-else-if="vazio" class="ia-cliente-contratos__vazio ia-texto-auxiliar">Nenhum contrato</p>

    <ul v-else class="ia-cliente-contratos__lista">
      <li v-for="item in itens" :key="item.chave">
        <icon-svg :name="ICONE" :size="TAMANHO_ICONE" color="var(--neutral-700)" />
        <span class="ia-cliente-contratos__nome ia-texto-auxiliar">{{ item.nome }}</span>
        <!-- Botão só quando há id: sem ele o download não tem alvo, e um
             ícone que não faz nada é pior que a ausência dele. -->
        <button
          v-if="item.id"
          type="button"
          class="ia-cliente-contratos__baixar"
          :disabled="Boolean(baixando)"
          :title="'Baixar ' + item.nome"
          :aria-label="'Baixar ' + item.nome"
          @click="baixar(item)"
        >
          <v-progress-circular
            v-if="baixando === item.id"
            indeterminate
            size="14"
            width="2"
            color="var(--primary)"
          />
          <icon-svg
            v-else
            :name="ICONE_BAIXAR"
            :size="TAMANHO_ICONE"
            color="var(--neutral-600)"
          />
        </button>
      </li>
    </ul>
  </div>
</template>

<style lang="sass" scoped>
.ia-cliente-contratos
  display: flex
  flex-direction: column
  gap: 10px

// Mesma régua do `.ia-insight__rotulo`.
//
// ⚠️ Sem chevron e sem clique: cada item abre o SEU destino, e um "ver mais"
// aqui teria de escolher um por todos. Abrir item a item é a leva seguinte.
.ia-cliente-contratos__rotulo
  color: var(--neutral-600)
  display: flex
  align-items: center
  gap: 4px
  width: 100%
  padding: 0
  background: transparent
  border: 0
  letter-spacing: 0.06em
  text-transform: uppercase

  &:focus-visible
    outline: 2px solid var(--primary)
    outline-offset: 2px

.ia-cliente-contratos__lista
  display: flex
  flex-direction: column
  gap: 8px
  margin: 0
  padding: 0
  list-style: none

  li
    display: flex
    align-items: center
    gap: 8px
    min-width: 0

.ia-cliente-contratos__pendente
  flex: 1
  min-width: 0

// ⚠️ 12px e peso explícito — a mesma régua do texto de estado vazio logo abaixo
// e da lista de transcrições. Sem o peso declarado, a linha herdava o do
// contexto e saía mais encorpada que as seções vizinhas.
.ia-cliente-contratos__nome
  color: var(--neutral-900)
  overflow: hidden
  text-overflow: ellipsis
  white-space: nowrap

// Encostado à direita, sem encolher: é o nome que cede espaço.
.ia-cliente-contratos__baixar
  display: flex
  align-items: center
  justify-content: center
  flex-shrink: 0
  margin-left: auto
  width: 24px
  height: 24px
  padding: 0
  border: 0
  border-radius: var(--radius-sm, 8px)
  background: transparent
  cursor: pointer

  &:hover:not(:disabled)
    background: var(--sidebar-warm-hover)

  &:disabled
    cursor: not-allowed

  &:focus-visible
    outline: 2px solid var(--primary)
    outline-offset: -2px

.ia-cliente-contratos__vazio
  color: var(--neutral-600)
  margin: 0
</style>
